import { useTranslation } from 'react-i18next';
import Switch from '@mui/material/Switch';
import {
  Button,
  FormControlLabel,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { GridColDef, GridRow, GridRowProps, useGridApiRef } from '@mui/x-data-grid';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { checkPermissions, PRODUCTS_WRITE } from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import styled from 'styled-components';
import { dateTimeLocal } from '../../shared/utils/dateHelpers';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { ProductDetails } from './ProductDetails';
import { Circle, ErrorOutline, Search } from '@mui/icons-material';
import Fuse from 'fuse.js';
import StyledDataGrid from '../../shared/DataGrid/StyledDataGrid';
import {
  useAllIngredients,
  useAllProductCategories,
  useAllProductPrices,
  useAllProducts,
  useAllProductSizes,
  useAllS4DIngredients,
  useAllS4DProducts,
  useSaveProductsDisplayOrder,
} from '../../apis/products-api';
import {
  ProductDto,
  ProductSizeDto,
  S4DProductDto,
  UpdateProductDisplayOrderDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { S4DStatusIndicator } from './components/S4DStatusIndicator';
import { S4DIssuesSummary } from './components/S4DIssuesSummary';
import { compareProductToS4D, S4DProductComparison } from './utils/s4dProductComparison';
import dayjs from 'dayjs';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GridCopyButton } from '../../shared/utils/gridUtils';
import { StyledRouterLink } from '../../shared/StyledLink/StyledLink';

const ProductsPage: React.FC = () => (
  <Routes>
    <Route path=":id" element={<ProductDetails />} />
    <Route index element={<ProductsList />} />
  </Routes>
);

// Only the products whose displayOrder changes are returned. Products hidden by the search or the
// inactive filter keep their displayOrder; the visible products swap the displayOrder values they
// already held. If those values are missing or duplicated, the whole list is renumbered from 1.
const getChangedDisplayOrders = (orderedRows: ProductDto[], allProducts: ProductDto[]) => {
  const slots = orderedRows.map((row) => row.displayOrder || 0).sort((a, b) => a - b);
  const slotsAreUnique = slots.every((slot, index) => slot > 0 && (index === 0 || slot > slots[index - 1]));

  let newOrder: UpdateProductDisplayOrderDto[];
  if (slotsAreUnique) {
    newOrder = orderedRows.map((row, index) => ({ productId: row.productId || -1, displayOrder: slots[index] }));
  } else {
    const visibleIds = new Set(orderedRows.map((row) => row.productId));
    const reordered = [...orderedRows];
    newOrder = allProducts
      .map((product) => (visibleIds.has(product.productId) ? reordered.shift() ?? product : product))
      .map((product, index) => ({ productId: product.productId || -1, displayOrder: index + 1 }));
  }

  const currentOrder = new Map([...allProducts, ...orderedRows].map((p) => [p.productId, p.displayOrder || 0]));
  return newOrder.filter((order) => currentOrder.get(order.productId) !== order.displayOrder);
};

const ProductsList: React.FC = () => {
  const [filteredRows, setFilteredRows] = useState<ProductDto[]>([]);
  const [showNotActive, toggleShowNotActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  // The rows being reordered; null outside ordering mode.
  const [orderedRows, setOrderedRows] = useState<ProductDto[] | null>(null);
  const isOrderingMode = orderedRows !== null;
  const [isSorted, setIsSorted] = useState(false);
  const apiRef = useGridApiRef();

  const sensors = useSensors(useSensor(PointerSensor));

  const { t } = useTranslation();
  const { roles: userPermissions } = useUserPermissions();
  const navigate = useNavigate();
  const saveDisplayOrder = useSaveProductsDisplayOrder({ onSuccess: () => setOrderedRows(null) });

  useEffect(() => {
    if (typeof apiRef.current?.subscribeEvent === 'function') {
      apiRef.current.subscribeEvent('sortModelChange', (sortModel) => {
        setIsSorted(sortModel.length > 0);
      });
    }
  }, [apiRef]);

  const { data: initialData, isLoading } = useAllProducts(true, {
    select: (data) => {
      const now = dayjs();

      return data
        ?.map((product) => {
          return {
            ...product,
            id: product.productId,
            displayOrder: product.displayOrder || 0,
            updatedCombined: `${product.updatedBy} ${dateTimeLocal(product.updated)}`,
            activeCombined:
              product.startDate && now < dayjs(product.startDate)
                ? dayjs(product.startDate).local().format('D.M.YYYY')
                : product.active
                ? 'active'
                : 'deactivated',
          };
        })
        .sort((a, b) => a.displayOrder - b.displayOrder);
    },
  });

  const fuse = useMemo(
    () =>
      new Fuse(initialData || [], {
        threshold: 0.2,
        ignoreLocation: true,
        keys: ['name', 'productId', 'description', 'productCategoryName', 'displayGroups.name'],
      }),
    [initialData]
  );

  useEffect(() => {
    const activeData = showNotActive ? initialData : initialData?.filter((x) => x.active);

    if (searchValue.length >= 3) {
      fuse.setCollection(activeData || []);
      setFilteredRows([...fuse.search(searchValue).map((result) => result.item)]);
    }
    if (searchValue.length < 3) {
      setFilteredRows(activeData || []);
    }
  }, [fuse, initialData, searchValue, showNotActive, setFilteredRows]);

  const notActiveCount = useMemo(() => (initialData ?? []).filter((product) => !product.active).length, [initialData]);

  // The S4D comparison data is large; fetch it only after the list itself has loaded.
  const s4dEnabled = Boolean(initialData);
  const s4dProductsQuery = useAllS4DProducts({ enabled: s4dEnabled });
  const s4dIngredientsQuery = useAllS4DIngredients({ enabled: s4dEnabled });
  const allIngredientsQuery = useAllIngredients(true, { enabled: s4dEnabled });
  // Product sizes are only fetched once the categories have arrived, so their errors count too.
  const allProductCategoriesQuery = useAllProductCategories({ enabled: s4dEnabled });
  const allProductSizesQuery = useAllProductSizes();
  const allProductPricesQuery = useAllProductPrices({ enabled: s4dEnabled });

  const { data: s4dProducts } = s4dProductsQuery;
  const { data: s4dIngredients } = s4dIngredientsQuery;
  const { data: allIngredients } = allIngredientsQuery;
  const { data: allProductSizes } = allProductSizesQuery;
  const { data: allProductPrices } = allProductPricesQuery;

  const failedS4DSources = useMemo(
    () =>
      [
        { label: t('S4D products'), error: s4dProductsQuery.error },
        { label: t('S4D toppings'), error: s4dIngredientsQuery.error },
        { label: t('Ingredients'), error: allIngredientsQuery.error },
        { label: t('nav.productCategories'), error: allProductCategoriesQuery.error },
        { label: t('Product sizes'), error: allProductSizesQuery.error },
        { label: t('Product prices'), error: allProductPricesQuery.error },
      ]
        .filter((source) => source.error)
        .map((source) => `${source.label}: ${(source.error as Error)?.message || t('global.error')}`),
    [
      t,
      s4dProductsQuery.error,
      s4dIngredientsQuery.error,
      allIngredientsQuery.error,
      allProductCategoriesQuery.error,
      allProductSizesQuery.error,
      allProductPricesQuery.error,
    ]
  );

  const { s4dComparisons, uncheckedPriceProductIds } = useMemo(() => {
    const comparisons = new Map<number, S4DProductComparison>();
    // Recipe-priced sizes have no minimum price, and their calculated price is not in the price rows.
    const uncheckedPrices = new Set<number>();

    if (!initialData || !s4dProducts || !s4dIngredients || !allIngredients || !allProductSizes || !allProductPrices) {
      return { s4dComparisons: comparisons, uncheckedPriceProductIds: uncheckedPrices };
    }

    const sizeIdsByProductId = new Map<number, Set<number>>();
    const ourPricesByProductId = new Map<number, Map<number, number>>();

    allProductPrices.forEach((price) => {
      const sizeIds = sizeIdsByProductId.get(price.productId) || new Set<number>();
      sizeIds.add(price.productSizeId);
      sizeIdsByProductId.set(price.productId, sizeIds);

      if (price.minimumPrice > 0) {
        const sizePrices = ourPricesByProductId.get(price.productId) || new Map<number, number>();
        sizePrices.set(price.productSizeId, price.minimumPrice);
        ourPricesByProductId.set(price.productId, sizePrices);
      } else {
        uncheckedPrices.add(price.productId);
      }
    });

    const s4dProductsByProductId = new Map<number, S4DProductDto[]>();
    s4dProducts.forEach((s4dProduct) => {
      if (s4dProduct.productId === undefined || s4dProduct.productId === null) return;
      const productS4DProducts = s4dProductsByProductId.get(s4dProduct.productId) || [];
      productS4DProducts.push(s4dProduct);
      s4dProductsByProductId.set(s4dProduct.productId, productS4DProducts);
    });

    const sizesByCategoryId = new Map<number, ProductSizeDto[]>();
    allProductSizes.forEach((size) => {
      const categorySizes = sizesByCategoryId.get(size.productCategoryId) || [];
      categorySizes.push(size);
      sizesByCategoryId.set(size.productCategoryId, categorySizes);
    });

    initialData.forEach((product) => {
      const productId = product.productId;
      if (!productId) return;

      const sizeIds = sizeIdsByProductId.get(productId);
      const productSizes = (sizesByCategoryId.get(product.productCategoryId) || []).filter((size) =>
        sizeIds?.has(size.productSizeId)
      );

      comparisons.set(
        productId,
        compareProductToS4D({
          product,
          s4dProducts: s4dProductsByProductId.get(productId) || [],
          s4dIngredients,
          ingredients: allIngredients,
          productSizes,
          ourPricesBySizeId: ourPricesByProductId.get(productId) || new Map<number, number>(),
        })
      );
    });

    return { s4dComparisons: comparisons, uncheckedPriceProductIds: uncheckedPrices };
  }, [initialData, s4dProducts, s4dIngredients, allIngredients, allProductSizes, allProductPrices]);

  const ingredientNamesById = useMemo(
    () => new Map(allIngredients?.map((ingredient) => [ingredient.ingredientId, ingredient.name])),
    [allIngredients]
  );
  const getIngredientName = useCallback(
    (ingredientId: number) => ingredientNamesById.get(ingredientId),
    [ingredientNamesById]
  );

  const renderS4DIndicator = (productId: number) => {
    const comparison = s4dComparisons.get(productId);
    if (comparison) {
      return (
        <S4DStatusIndicator
          comparison={comparison}
          getIngredientName={getIngredientName}
          hasUncheckedPrices={uncheckedPriceProductIds.has(productId)}
        />
      );
    }

    if (failedS4DSources.length > 0) {
      return (
        <Tooltip title={t('S4D comparison failed. {{sources}}', { sources: failedS4DSources.join(', ') })}>
          <ErrorOutline sx={{ fontSize: 12 }} color="error" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t('Fetching S4D data')}>
        <Circle sx={{ fontSize: 10 }} color="disabled" />
      </Tooltip>
    );
  };

  let columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      flex: 1,
      renderCell: (params) => {
        if (isOrderingMode) return params.id;
        return (
          <>
            <GridCopyButton params={params} />{' '}
            <StyledRouterLink to={`/products/${params.id}`}>{params.value}</StyledRouterLink>
          </>
        );
      },
    },
    {
      field: 'name',
      headerName: t('product.product'),
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            {renderS4DIndicator(params.row.productId)}
            {isOrderingMode ? (
              params.value
            ) : (
              <StyledRouterLink to={`/products/${params.id}`}>{params.value}</StyledRouterLink>
            )}
          </Box>
        );
      },
      width: 190,
      flex: 2,
    },
    {
      field: 'productCategoryName',
      headerName: t('Product category'),
      width: 100,
      flex: 2,
    },
    {
      field: 'displayGroups',
      headerName: t('productDetails.displayGroups'),
      width: 100,
      flex: 2,
      renderCell: (params) => {
        const product: ProductDto = params.row;
        return <>{product.displayGroups?.map((d) => d.name).join(', ')}</>;
      },
    },
    { field: 'description', headerName: t('product.description'), width: 800, flex: 6 },
    {
      field: 'activeCombined',
      headerName: t('Status'),
      flex: 1,
      renderCell: (params) => {
        return params.value === 'active' ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : params.value === 'deactivated' ? (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        ) : (
          <Chip color="warning" label={params.value} />
        );
      },
    },
    { field: 'updatedCombined', headerName: t('product.updated'), width: 170, flex: 2 },
  ];

  columns = columns.map((c) => ({
    ...c,
    sortable: !isOrderingMode,
  }));

  const rows = orderedRows ?? filteredRows;

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over) {
      setOrderedRows((current) => {
        if (!current) return current;
        const oldIndex = current.findIndex((row) => row.productId === active.id);
        const newIndex = current.findIndex((row) => row.productId === over.id);
        return arrayMove(current, oldIndex, newIndex);
      });
    }
  }, []);

  const handleOrderClick = () => {
    if (!orderedRows) {
      // Search results come in relevance order; start from the saved order instead.
      setOrderedRows([...filteredRows].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
      return;
    }

    const request = getChangedDisplayOrders(orderedRows, initialData || []);
    if (request.length === 0) {
      setOrderedRows(null);
      return;
    }
    saveDisplayOrder.mutate(request);
  };

  const handleNewProduct = () => {
    navigate('/products/new');
  };

  return (
    <>
      <TopBar>
        <Typography variant="h1">{t('product.title')}</Typography>
        <Box sx={{ justifySelf: 'flex-end' }}>
          {isOrderingMode && (
            <Button
              variant="outlined"
              color="error"
              sx={{ mr: 2 }}
              onClick={() => setOrderedRows(null)}
              disabled={saveDisplayOrder.isLoading}
            >
              {t('global.cancel')}
            </Button>
          )}
          <Button
            variant={isOrderingMode ? 'contained' : 'outlined'}
            onClick={handleOrderClick}
            sx={{ mr: 2 }}
            disabled={isSorted || saveDisplayOrder.isLoading}
          >
            {isOrderingMode ? t('global.save') : t('global.sort')}
          </Button>
          <Button
            disabled={!checkPermissions(userPermissions || [], [PRODUCTS_WRITE]) || isOrderingMode}
            variant="contained"
            onClick={handleNewProduct}
            color="primary"
          >
            {t('product.addProduct')}
          </Button>
        </Box>
      </TopBar>
      <StyledToolbar variant="outlined">
        <Grid display="flex" container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SearchInput
              id="search-products"
              placeholder={t('global.search')}
              variant="outlined"
              value={searchValue}
              disabled={isOrderingMode}
              autoComplete="off"
              onChange={(e) => {
                e.preventDefault();
                setSearchValue(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid display="flex" alignItems="center" gap={2} item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showNotActive}
                  disabled={isOrderingMode}
                  onChange={() => toggleShowNotActive((prev) => !prev)}
                />
              }
              label={`${t('product.showInactive')} (${notActiveCount} kpl)`}
            />
            <S4DIssuesSummary
              products={initialData || []}
              s4dProducts={s4dProducts || []}
              comparisons={s4dComparisons}
              getIngredientName={getIngredientName}
              failedSources={failedS4DSources}
            />
          </Grid>
        </Grid>
      </StyledToolbar>
      <DataGridContainer display="flex">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges, restrictToParentElement]}
          autoScroll={false}
        >
          <SortableContext
            items={rows.map((r) => ({ id: r.productId || -1 }))}
            strategy={verticalListSortingStrategy}
            disabled={!isOrderingMode}
          >
            <StyledDataGrid
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.productId}
              loading={isLoading}
              components={{ Row: DraggableGridRow }}
              getRowClassName={(params) => (params.row.active ? '' : 'grid-row-not-active')}
            />
          </SortableContext>
        </DndContext>
      </DataGridContainer>
    </>
  );
};

const DraggableGridRow = memo((params: GridRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: params.rowId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <GridRow {...params} />
    </div>
  );
});

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 15px 0;
`;

const StyledToolbar = styled(Paper)`
  margin-bottom: 1em;
  display: flex;
  padding: 1em;
`;

const SearchInput = styled(TextField)`
  margin-right: 2em;
  flex: 0 1 500px;
  width: 100%;
`;

const DataGridContainer = styled(Box)`
  height: calc(100vh - 300px);
`;

export default ProductsPage;
